import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Grid, MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef } from "material-react-table"
import { FetchTenantAdminsResponse, fetchTenantAdmins, TenantAdminQuery } from "../tenantAdminApi"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useApiQueryTable } from "../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import { Button } from "./Button"
import { DeleteConfirmDialog } from "../../../../shared/components/dialogs/DeleteConfirmDialog"
import { UserDetailsDialog } from "./UserDetails/UserDetailsDialog"
import { getEditTenantAdminRoute, TENANT_USERS_CREATE_ADMIN_ROUTE } from "../../../../constants/routes"
import { Admin, TenantOfficeManager } from "../../../users/userTypes"
import { useApiMutation } from "../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../core/api/errorTypes"
import { deleteAdmin } from "../../../users/userApi"
import { useCurrentTenant } from "../../../tenants/tenantHooks"
import { DestructiveMenuItem } from "../../../../shared/components/menus/DestructiveMenuItem"
import {
    ImpersonationConfirmDialog,
    type ImpersonationTarget,
    toImpersonationTarget,
} from "../../../../shared/components/dialogs/ImpersonationConfirmDialog"
import { useCanImpersonate } from "../../../../shared/hooks/useCanImpersonate"

function transformData(data: FetchTenantAdminsResponse) {
    return data.administrators
}

const useStyles = makeStyles()((_theme) => ({
    page: {
        paddingTop: "3rem",
    },
    customerTable: {
        marginTop: "3rem",
    },
}))

export function TenantAdminsTab() {
    const { classes } = useStyles()

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [user, setUser] = useState<Admin>()
    const [impersonationTarget, setImpersonationTarget] = useState<ImpersonationTarget | null>(null)
    const canImpersonate = useCanImpersonate()
    const navigate = useNavigate()

    const { l10n } = useAppLocalization()
    const { companyId, userGroupId } = useCurrentTenant()

    const tenantAdmins = useApiQuery(fetchTenantAdmins, {
        queryName: TenantAdminQuery.ADMINS_FOR_TENANT,
        dependencies: {
            companyId: companyId,
            userGroupId: userGroupId,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(tenantAdmins)

    const { mutateAsync: callDeleteAdmin } = useApiMutation(deleteAdmin, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TenantAdminQuery.ADMINS_FOR_TENANT] })
        },
    })

    function handleDeleteClick() {
        if (user === undefined) {
            return
        } else {
            callDeleteAdmin({
                userId: user.user.id,
            })
        }
    }

    const columns = useMemo<MRT_ColumnDef<TenantOfficeManager>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("office-manager-users-table-name-column"),
                accessorFn: (row) => row.user.displayName,
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("office-manager-users-table-email-column"),
                accessorKey: "user.email",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "is-active",
                    header: l10n.getString("office-manager-users-table-status-column"),
                },
                (row) => row.user.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <Grid container>
                <Grid item container xs={12} direction="row" justifyContent="flex-end" mt={3}>
                    <LocalizedStrict id="office-manager-users-create-admin-button">
                        <Button
                            variant="contained"
                            onClick={() => {
                                navigate(TENANT_USERS_CREATE_ADMIN_ROUTE)
                            }}
                        >
                            Create new
                        </Button>
                    </LocalizedStrict>
                </Grid>
                <Grid item xs={12}>
                    <ApiQueryTable
                        columns={columns}
                        muiTablePaperProps={{
                            className: classes.customerTable,
                        }}
                        renderRowActionMenuItems={({ row, closeMenu }) => [
                            <LocalizedStrict id="office-manager-user-show-details">
                                <MenuItem
                                    key={row.original.user.id}
                                    onClick={() => {
                                        setUser(row.original)
                                        setShowDetailsModal(true)
                                        closeMenu()
                                    }}
                                >
                                    Show details
                                </MenuItem>
                            </LocalizedStrict>,
                            <LocalizedStrict id="office-manager-user-edit">
                                <MenuItem
                                    key={row.original.user.id}
                                    onClick={() => {
                                        navigate(getEditTenantAdminRoute(row.original.user.id))
                                        closeMenu()
                                    }}
                                >
                                    Edit user
                                </MenuItem>
                            </LocalizedStrict>,
                            canImpersonate && (
                                <LocalizedStrict
                                    key={`impersonate-${row.original.user.id}`}
                                    id="office-manager-admins-table-button-impersonate"
                                >
                                    <MenuItem
                                        onClick={() => {
                                            setImpersonationTarget(toImpersonationTarget(row.original.user))
                                            closeMenu()
                                        }}
                                    >
                                        Log in as
                                    </MenuItem>
                                </LocalizedStrict>
                            ),
                            <LocalizedStrict id="office-manager-user-delete">
                                <DestructiveMenuItem
                                    key={row.original.user.id}
                                    onClick={() => {
                                        setUser(row.original)
                                        setShowDeleteModal(true)
                                        closeMenu()
                                    }}
                                >
                                    Delete user
                                </DestructiveMenuItem>
                            </LocalizedStrict>,
                        ]}
                    />

                    {user && (
                        <DeleteConfirmDialog
                            typeOfEntity={l10n.getString("office-manager-user-delete-title")}
                            entityName={user.user.displayName}
                            handleClick={handleDeleteClick}
                            open={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                        />
                    )}

                    {user && (
                        <UserDetailsDialog
                            key={user.user.id}
                            userId={user?.user.id}
                            userType="admin"
                            open={showDetailsModal}
                            onClose={() => {
                                setShowDetailsModal(false)
                            }}
                        />
                    )}

                    <ImpersonationConfirmDialog
                        target={impersonationTarget}
                        onClose={() => setImpersonationTarget(null)}
                    />
                </Grid>
            </Grid>
        </>
    )
}
